// Catalog.ID Client — ceremony intake and renderers (docs/cid-governance.md M6)
// ============================================================================
// What the ceremony console accepts and what it shows. The organization's
// `main_signing_key` (or a person's own) signs four kinds of document here,
// each read by the shared library (@catalog-federation, the twin of
// libs/federation) and taken to the Developer's `cl-root`:
//
//   • a certificate request a node's setup wrote (`cpr` or `cid`), which the
//     organization countersigns (D8);
//   • the organization's binding consent to an operator code (D6);
//   • an account's voluntary origin migration consent (D20);
//   • the voluntary bulk migration consent of an operator's organization (D20).
//
// TWO LOAD-BEARING RULES LIVE HERE:
//
//  1. Blind-signing guard. The console signs only documents whose type has an
//     entry in CEREMONY_TYPES — a strict reader and a renderer. Anything else
//     is refused before it is shown, and a document carrying a field the
//     reader does not know is refused too: bytes the console cannot render are
//     bytes nobody can consent to. The same `main_signing_key` signs many
//     artefacts; without this the console is "sign arbitrary bytes with the
//     organization key".
//
//  2. Canonical bytes. A delivered file must already BE its RFC 8785 form,
//     byte for byte (the library's `parseCanonical`): a pretty-printed,
//     re-ordered or re-encoded file is refused, never "corrected" and signed.
//     DECISION (M6): JSON whitespace — space, tab, CR, LF — before and after
//     the document is transport noise and is trimmed first (an editor's final
//     newline, a paste). Nothing else is forgiven, anywhere: not inside the
//     document, not a byte-order mark, not a no-break space. Trimming changes
//     nothing that is signed or sent on: the console signs bytes it rebuilds
//     from the strictly read fields and writes canonical files itself.
//
// A request file's proofs of possession are checked here too, before anything
// is rendered (`checkCertificateRequest(file, null, …)`): a request whose keys
// did not sign it is not something to countersign.

import {
    ACCOUNT_MIGRATION_CONSENT_TYPE,
    BINDING_CONSENT_TYPE,
    BULK_MIGRATION_CONSENT_TYPE,
    CERTIFICATE_REQUEST_TYPE,
    FederationFileError,
    checkCertificateRequest,
    formatFingerprint,
    publicKeyFingerprint,
    readAccountMigrationConsentPayload,
    readBindingConsentPayload,
    readBulkMigrationConsentPayload,
    readCertificateRequestFile,
    type AccountMigrationConsent,
    type BindingConsent,
    type BulkMigrationConsent,
    type SignatureVerifier,
    type SignedCertificateRequest,
} from '@catalog-federation';

// ── documents ───────────────────────────────────────────────────────────

/** A document the console can sign, read strictly from canonical bytes. */
export type CeremonyDocument =
    | { kind: 'certificate_countersignature'; request: SignedCertificateRequest }
    | { kind: 'binding_consent'; consent: BindingConsent }
    | { kind: 'account_migration_consent'; consent: AccountMigrationConsent }
    | { kind: 'bulk_migration_consent'; consent: BulkMigrationConsent };

export type CeremonyKind = CeremonyDocument['kind'];

export type CeremonyRefusalCode =
    | 'invalid-json'
    | 'not-an-object'
    | 'unsupported-type'
    | 'already-signed'
    | 'non-canonical'
    | 'malformed'
    | 'proof-failed';

export interface CeremonyRefusal {
    code: CeremonyRefusalCode;
    message: string;
}

export type IntakeResult =
    | { ok: true; document: CeremonyDocument }
    | { ok: false; refusal: CeremonyRefusal };

// ── rendering ───────────────────────────────────────────────────────────

/** One key the document names, fingerprint in the D17 form (two lines). */
export interface RenderedKey {
    /** `cid.sync`, `cpr.block`, `organization key`, … */
    label: string;
    /** Extra facts about the key (the `jwt` key's kid). */
    detail?: string;
    fingerprint: string;
}

/** What the console shows before anything is signed. */
export interface CeremonyRendering {
    title: string;
    /** What signing means, in plain words. */
    summary: string;
    facts: { label: string; value: string }[];
    keys: RenderedKey[];
    /** Whose key signs: the organization's, or the account's own. */
    signer: 'organization' | 'account';
}

interface CeremonyType {
    kind: CeremonyKind;
    read(bytes: Uint8Array): CeremonyDocument;
    render(doc: CeremonyDocument): Promise<CeremonyRendering>;
}

/**
 * The blind-signing allowlist: a document type is signable only with an entry
 * here — a strict reader and a renderer. Keyed by the payload `type` (for a
 * request file, the `type` of its `request`).
 */
const CEREMONY_TYPES: Readonly<Record<string, CeremonyType>> = Object.freeze({
    [CERTIFICATE_REQUEST_TYPE]: {
        kind: 'certificate_countersignature',
        read: bytes => ({ kind: 'certificate_countersignature', request: readCertificateRequestFile(bytes) }),
        render: doc => renderCountersignature(as(doc, 'certificate_countersignature').request),
    },
    [BINDING_CONSENT_TYPE]: {
        kind: 'binding_consent',
        read: bytes => ({ kind: 'binding_consent', consent: readBindingConsentPayload(bytes) }),
        render: doc => renderBindingConsent(as(doc, 'binding_consent').consent),
    },
    [ACCOUNT_MIGRATION_CONSENT_TYPE]: {
        kind: 'account_migration_consent',
        read: bytes => ({ kind: 'account_migration_consent', consent: readAccountMigrationConsentPayload(bytes) }),
        render: doc => renderAccountMigrationConsent(as(doc, 'account_migration_consent').consent),
    },
    [BULK_MIGRATION_CONSENT_TYPE]: {
        kind: 'bulk_migration_consent',
        read: bytes => ({ kind: 'bulk_migration_consent', consent: readBulkMigrationConsentPayload(bytes) }),
        render: doc => renderBulkMigrationConsent(as(doc, 'bulk_migration_consent').consent),
    },
});

/** Document types this build renders and signs. */
export const SUPPORTED_CEREMONY_TYPES: readonly string[] = Object.freeze(Object.keys(CEREMONY_TYPES));

/** The payload type of a document (the registry key). */
export function ceremonyType(doc: CeremonyDocument): string {
    switch (doc.kind) {
        case 'certificate_countersignature': return CERTIFICATE_REQUEST_TYPE;
        case 'binding_consent': return BINDING_CONSENT_TYPE;
        case 'account_migration_consent': return ACCOUNT_MIGRATION_CONSENT_TYPE;
        case 'bulk_migration_consent': return BULK_MIGRATION_CONSENT_TYPE;
    }
}

/** What the console shows for `doc`, through its registered renderer. */
export function renderCeremonyDocument(doc: CeremonyDocument): Promise<CeremonyRendering> {
    const type = CEREMONY_TYPES[ceremonyType(doc)];
    if (!type) throw new Error(`no renderer for ${ceremonyType(doc)}`);
    return type.render(doc);
}

// ── intake ──────────────────────────────────────────────────────────────

const JSON_WHITESPACE = new Set([0x20, 0x09, 0x0a, 0x0d]);

/** `bytes` without the JSON whitespace (space, tab, LF, CR) before and after it. */
export function trimTransportWhitespace(bytes: Uint8Array): Uint8Array {
    let start = 0;
    let end = bytes.length;
    while (start < end && JSON_WHITESPACE.has(bytes[start]!)) start++;
    while (end > start && JSON_WHITESPACE.has(bytes[end - 1]!)) end--;
    return bytes.subarray(start, end);
}

/**
 * Reads a delivered ceremony file (a file's bytes or pasted text): identifies
 * it, refuses what the console will not sign, checks canonical bytes, reads it
 * strictly and — for a request file — checks every proof of possession.
 */
export async function readCeremonyInput(
    input: string | Uint8Array, signatures: SignatureVerifier,
): Promise<IntakeResult> {
    const bytes = trimTransportWhitespace(typeof input === 'string' ? new TextEncoder().encode(input) : input);

    // A lenient parse only to find the discriminator; the strict, canonical
    // read below is what the document is taken from.
    let parsed: unknown;
    try {
        parsed = JSON.parse(new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes));
    } catch {
        return refuse('invalid-json',
            'This is not a ceremony file: it is not JSON. Open the file exactly as you received it.');
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return refuse('not-an-object', 'This is not a ceremony file: a ceremony file is a JSON object.');
    }
    const o = parsed as Record<string, unknown>;

    if (isObject(o['consent']) && ('org_signature' in o || 'account_signature' in o)) {
        return refuse('already-signed',
            'This consent is already signed. Nothing more is needed here: send the file to the Developer as it is.');
    }
    const discriminator = typeof o['type'] === 'string'
        ? o['type']
        : isObject(o['request']) && typeof o['request']['type'] === 'string' ? o['request']['type'] : null;
    if (discriminator === CERTIFICATE_REQUEST_TYPE && !isObject(o['request'])) {
        return refuse('unsupported-type',
            'This is a certificate request without its proofs of possession. Open the request FILE the node\'s '
            + 'setup wrote (…-certificate-request.json), not the request printed inside it.');
    }
    const type = discriminator === null ? undefined : CEREMONY_TYPES[discriminator];
    if (!type) {
        return refuse('unsupported-type',
            (discriminator === null
                ? 'This file does not say what it is (no "type"), so it cannot be shown or signed.'
                : `This console will not sign a document of type "${discriminator}": it cannot show you what it says.`)
            + ` It signs: ${SUPPORTED_CEREMONY_TYPES.join(', ')}.`);
    }
    if (type.kind === 'certificate_countersignature' && isObject(o['organization'])) {
        const by = typeof o['organization']['org_cid'] === 'string' ? ` by ${o['organization']['org_cid']}` : '';
        return refuse('already-signed',
            `This certificate request is already countersigned${by}. Send it to the Developer as it is; `
            + 'a request is countersigned once.');
    }

    let document: CeremonyDocument;
    try {
        document = type.read(bytes);
    } catch (e) {
        if (e instanceof FederationFileError) {
            return e.code === 'non_canonical'
                ? refuse('non-canonical',
                    'This file is not in the exact form it was written in: it has been reformatted, re-ordered '
                    + 'or re-encoded on the way (for example pretty-printed). Nothing has been signed. Ask for the '
                    + 'file again and open it here without editing it.')
                : refuse('malformed', `This file cannot be signed: ${e.message}`);
        }
        throw e;
    }

    if (document.kind === 'certificate_countersignature') {
        const refusal = await checkCertificateRequest(document.request, null, signatures);
        if (refusal !== null) {
            return refuse('proof-failed',
                `This certificate request does not prove possession of its keys (${refusal.code}: ${refusal.detail}). `
                + 'Nothing has been signed. Ask the node operator to write the request again with the node\'s setup.');
        }
    }
    return { ok: true, document };
}

function refuse(code: CeremonyRefusalCode, message: string): IntakeResult {
    return { ok: false, refusal: { code, message } };
}

function isObject(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function as<K extends CeremonyKind>(doc: CeremonyDocument, kind: K): Extract<CeremonyDocument, { kind: K }> {
    if (doc.kind !== kind) throw new Error(`expected a ${kind}, got a ${doc.kind}`);
    return doc as Extract<CeremonyDocument, { kind: K }>;
}

// ── renderers ───────────────────────────────────────────────────────────

async function fingerprintOf(publicKey: Uint8Array): Promise<string> {
    return formatFingerprint(await publicKeyFingerprint(publicKey));
}

const KIND_NAMES: Record<string, string> = {
    cpr: 'a Catalog Public Registry (CPR) node',
    cid: 'a Catalog.ID node',
};

/** The title's short name of each kind; a kind without one is `<kind> node`, never another kind's name. */
const KIND_SHORT_NAMES: Record<string, string> = {
    cpr: 'CPR node',
    cid: 'Catalog.ID node',
};

async function renderCountersignature(s: SignedCertificateRequest): Promise<CeremonyRendering> {
    const r = s.request;
    const keys: RenderedKey[] = [];
    for (const name of Object.keys(r.keys).sort()) {
        const key = r.keys[name]!;
        keys.push({
            label: `${r.kind}.${name}`,
            ...(key.kid !== undefined ? { detail: `kid ${key.kid}` } : {}),
            fingerprint: await fingerprintOf(key.publicKey),
        });
    }
    return {
        title: `Certificate request — ${KIND_SHORT_NAMES[r.kind] ?? `${r.kind} node`}`,
        summary:
            `Operator ${r.operatorId} asks the Developer to certify the keys of ${KIND_NAMES[r.kind] ?? `a ${r.kind} node`}. `
            + 'Its keys have signed the request themselves. Countersigning adds your organization\'s signature: it '
            + `says that your organization, bound to operator ${r.operatorId}, stands behind this node. The Developer `
            + 'certifies the keys only after you read each fingerprint below back to them over a second channel.',
        facts: [
            { label: 'Operator code', value: r.operatorId },
            { label: 'Service', value: `${r.kind} — ${KIND_NAMES[r.kind] ?? r.kind}` },
            { label: 'Node', value: r.nodeUuid },
            { label: 'Requested at', value: r.requestedAt },
        ],
        keys,
        signer: 'organization',
    };
}

async function renderBindingConsent(c: BindingConsent): Promise<CeremonyRendering> {
    return {
        title: 'Binding consent',
        summary:
            `Organization ${c.orgCid} consents to be bound to operator code ${c.operatorId}: from ${c.effectiveFrom}, `
            + `it holds the operator powers of ${c.operatorId} and signs its requests. The Developer signs the binding `
            + 'record after reading the organization key fingerprint below back to you. The binding carries this key; '
            + 'if the organization\'s signing key ever changes, a new binding is needed before further requests.',
        facts: [
            { label: 'Operator code', value: c.operatorId },
            { label: 'Organization', value: c.orgCid },
            { label: 'Sequence', value: String(c.sequence) },
            { label: 'Effective from', value: c.effectiveFrom },
        ],
        keys: [{ label: 'organization key (main_signing_key)', fingerprint: await fingerprintOf(c.orgPublicKey) }],
        signer: 'organization',
    };
}

async function renderAccountMigrationConsent(c: AccountMigrationConsent): Promise<CeremonyRendering> {
    return {
        title: 'Origin migration consent',
        summary:
            `Account ${c.accountCid} consents to move its origin from operator ${c.fromOperatorId} to operator `
            + `${c.toOperatorId}. After the Developer publishes the move, the account signs in at ${c.toOperatorId}'s `
            + 'node, where it enrolls again (password, key proof, device); sessions here end.',
        facts: [
            { label: 'Account', value: c.accountCid },
            { label: 'From operator', value: c.fromOperatorId },
            { label: 'To operator', value: c.toOperatorId },
            { label: 'Sequence', value: String(c.sequence) },
        ],
        keys: [{ label: 'account key (main_signing_key)', fingerprint: await fingerprintOf(c.accountPublicKey) }],
        signer: 'account',
    };
}

async function renderBulkMigrationConsent(c: BulkMigrationConsent): Promise<CeremonyRendering> {
    return {
        title: 'Bulk origin migration consent',
        summary:
            `Organization ${c.orgCid}, bound to operator ${c.fromOperatorId}, consents to move every account whose `
            + `origin is ${c.fromOperatorId} to operator ${c.toOperatorId} — including accounts that reach a node `
            + 'later. This is a standing redirect: it cannot be limited to some accounts, and every moved account '
            + 'enrolls again at the new origin.',
        facts: [
            { label: 'From operator', value: c.fromOperatorId },
            { label: 'To operator', value: c.toOperatorId },
            { label: 'Organization', value: c.orgCid },
            { label: 'Sequence', value: String(c.sequence) },
        ],
        keys: [{ label: 'organization key (main_signing_key)', fingerprint: await fingerprintOf(c.orgPublicKey) }],
        signer: 'organization',
    };
}
